import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const ORANGE = "#c4622d";
const PIE_COLORS = [
  "#c4622d", "#e8956d", "#2d7fc4", "#6db8e8",
  "#2dc47f", "#f0c040", "#9b59b6", "#e74c3c",
  "#1abc9c", "#f39c12",
];
// Palette for multi-series line/bar charts (e.g. one series per distance).
const SERIES_COLORS = [
  "#c4622d", "#2d7fc4", "#2dc47f", "#9b59b6",
  "#f0c040", "#e74c3c", "#1abc9c", "#34495e",
  "#f39c12", "#16a085",
];

function isTimeString(val) {
  return typeof val === "string" && /^\d+:\d{2}:\d{2}$/.test(val);
}

function timeToSeconds(t) {
  const [h, m, s] = t.split(":").map(Number);
  return h * 3600 + m * 60 + s;
}

function secondsToTime(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}

function useChartData(chart, columns, rows) {
  return useMemo(() => {
    const { x, y, series } = chart;
    const xIdx = columns.indexOf(x);
    const yIdx = columns.indexOf(y);
    if (xIdx === -1 || yIdx === -1) {
      return { data: [], isTimeY: false, seriesKeys: [] };
    }

    const firstYVal = rows.find((r) => r[yIdx] != null)?.[yIdx];
    const timeY = isTimeString(firstYVal);
    const sIdx = series ? columns.indexOf(series) : -1;

    // Single-series path: data rows look like { x, y }.
    if (sIdx === -1) {
      const data = rows
        .map((row) => {
          const rawY = row[yIdx];
          const numY = timeY ? timeToSeconds(rawY) : Number(rawY);
          return { x: row[xIdx], y: isNaN(numY) ? null : numY };
        })
        .filter((d) => d.y !== null);
      return { data, isTimeY: timeY, seriesKeys: [] };
    }

    // Multi-series path: pivot so each row is { x, [seriesVal1]: n, [seriesVal2]: n, ... }.
    // Preserve x insertion order (important for time progressions) and series
    // first-seen order (stable color assignment).
    const byX = new Map();
    const seriesOrder = [];
    const seenSeries = new Set();
    for (const row of rows) {
      const rawY = row[yIdx];
      const numY = timeY ? timeToSeconds(rawY) : Number(rawY);
      if (isNaN(numY)) continue;
      const xVal = row[xIdx];
      const sVal = row[sIdx];
      if (sVal == null) continue;
      const sKey = String(sVal);
      if (!seenSeries.has(sKey)) {
        seenSeries.add(sKey);
        seriesOrder.push(sKey);
      }
      if (!byX.has(xVal)) byX.set(xVal, { x: xVal });
      byX.get(xVal)[sKey] = numY;
    }
    return {
      data: Array.from(byX.values()),
      isTimeY: timeY,
      seriesKeys: seriesOrder,
    };
  }, [chart, columns, rows]);
}

function XYChart({ type, chart, columns, rows }) {
  const { data, isTimeY, seriesKeys } = useChartData(chart, columns, rows);
  if (!data.length) return null;

  const { y } = chart;
  const isMultiSeries = seriesKeys.length > 0;
  const yTickFormatter = isTimeY ? secondsToTime : undefined;
  const tooltipFormatter = (val, name) => [
    isTimeY ? secondsToTime(val) : val,
    isMultiSeries ? name : y,
  ];

  const margin = { top: 8, right: 24, left: 8, bottom: 64 };
  const xAxis = (
    <XAxis dataKey="x" angle={-40} textAnchor="end" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
  );
  const yAxis = (
    <YAxis tickFormatter={yTickFormatter} tick={{ fontSize: 11 }} width={isTimeY ? 60 : 40} />
  );
  const grid = <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />;
  const tooltip = (
    <Tooltip formatter={tooltipFormatter} labelFormatter={(l) => `${chart.x}: ${l}`} contentStyle={{ fontSize: "0.82rem" }} />
  );
  const legend = isMultiSeries ? (
    <Legend wrapperStyle={{ fontSize: "0.78rem" }} />
  ) : null;

  if (type === "line") {
    return (
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={data} margin={margin}>
          {grid}{xAxis}{yAxis}{tooltip}{legend}
          {isMultiSeries ? (
            seriesKeys.map((key, i) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3, fill: SERIES_COLORS[i % SERIES_COLORS.length] }}
                name={key}
                connectNulls
              />
            ))
          ) : (
            <Line type="monotone" dataKey="y" stroke={ORANGE} strokeWidth={2} dot={{ r: 3, fill: ORANGE }} name={y} />
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // bar and histogram — histogram just removes the category gap so bars touch
  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} margin={margin} barCategoryGap={type === "histogram" ? 0 : "10%"}>
        {grid}{xAxis}{yAxis}{tooltip}{legend}
        {isMultiSeries ? (
          seriesKeys.map((key, i) => (
            <Bar
              key={key}
              dataKey={key}
              fill={SERIES_COLORS[i % SERIES_COLORS.length]}
              radius={type === "histogram" ? 0 : [3, 3, 0, 0]}
              name={key}
            />
          ))
        ) : (
          <Bar dataKey="y" fill={ORANGE} radius={type === "histogram" ? 0 : [3, 3, 0, 0]} name={y} />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}

function PieChartView({ chart, columns, rows }) {
  const { x, y } = chart;
  const xIdx = columns.indexOf(x);
  const yIdx = columns.indexOf(y);
  if (xIdx === -1 || yIdx === -1) return null;

  const data = rows
    .map((row) => ({ name: String(row[xIdx]), value: Number(row[yIdx]) }))
    .filter((d) => !isNaN(d.value) && d.value > 0);

  if (!data.length) return null;

  const total = data.reduce((s, d) => s + d.value, 0);
  const renderLabel = ({ name, value }) =>
    `${name} (${((value / total) * 100).toFixed(1)}%)`;

  return (
    <ResponsiveContainer width="100%" height={320}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={110}
          label={renderLabel}
          labelLine={true}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(val) => [val, y]} contentStyle={{ fontSize: "0.82rem" }} />
        <Legend wrapperStyle={{ fontSize: "0.82rem" }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default function ChartView({ chart, columns, rows }) {
  const { type, title } = chart;

  const inner =
    type === "pie" ? (
      <PieChartView chart={chart} columns={columns} rows={rows} />
    ) : (
      <XYChart type={type} chart={chart} columns={columns} rows={rows} />
    );

  if (!inner) return null;

  return (
    <div style={styles.wrapper}>
      {title && <div style={styles.title}>{title}</div>}
      {inner}
    </div>
  );
}

const styles = {
  wrapper: { padding: "16px 8px 8px" },
  title: {
    textAlign: "center",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#444",
    marginBottom: "8px",
  },
};
